import { useQuery } from "@tanstack/react-query";
import { ArrowDownTrayIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import { useNavigate } from "react-router-dom";
import { r1Api } from "../../services/api";
import { R1_BASE_URL } from "../../services/api";
import { PortfolioCommentCell } from "../../portfolio/ui/PortfolioCommentCell";

function formatDate(value: unknown): string {
  if (!value) return "";
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return d.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function Cell({ children }: { children: React.ReactNode }) {
  return <td className="px-4 py-3 align-top">{children}</td>;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-500 whitespace-nowrap">
      {children}
    </th>
  );
}

function DateCell({
  value,
  fileName,
  uploadId,
}: {
  value: unknown;
  fileName?: string | null;
  uploadId?: number | null;
}) {
  const navigate = useNavigate();
  if (!value)
    return <span className="text-xs italic text-slate-400">Not received</span>;
  const downloadUrl = fileName
    ? `${R1_BASE_URL}/r1_data_upload/download-by-filename?file_name=${encodeURIComponent(fileName)}`
    : null;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="text-sm text-slate-800">{formatDate(value)}</span>
      {downloadUrl && (
        <a
          href={downloadUrl}
          title={fileName ?? "Download"}
          className="text-slate-400 hover:text-blue-600 transition-colors"
        >
          <ArrowDownTrayIcon className="h-3.5 w-3.5" />
        </a>
      )}
      {uploadId && (
        <button
          onClick={() => {
            const base = window.location.pathname.replace(/\/r1_[^/]+.*$/, "");
            navigate(`${base}/r1_data_upload/${uploadId}`);
          }}
          title="View details"
          className="text-slate-400 hover:text-blue-600 transition-colors"
        >
          <InformationCircleIcon className="h-3.5 w-3.5" />
        </button>
      )}
    </span>
  );
}

function BoolCell({ value }: { value: unknown }) {
  if (value === null || value === undefined)
    return <span className="text-xs text-slate-300">—</span>;
  return value ? (
    <span className="text-sm font-semibold text-emerald-600">Yes</span>
  ) : (
    <span className="text-sm text-slate-400">No</span>
  );
}

function TextCell({ value }: { value: unknown }) {
  if (value === null || value === undefined || value === "")
    return <span className="text-xs text-slate-300">—</span>;
  return <span className="text-sm text-slate-800">{String(value)}</span>;
}

export function TrackerSection({
  fundId,
  quarter,
}: {
  fundId: number;
  quarter: string;
}) {
  const { data, isLoading } = useQuery({
    queryKey: ["r1", "tracker-row", fundId, quarter],
    queryFn: () => r1Api.getFundsPortfolioRow(fundId, quarter),
    staleTime: 60_000,
  });

  const row = (data?.data ?? [])[0] as Record<string, unknown> | undefined;

  if (isLoading) return null;
  if (!row) return null;

  const pdfReceived = row.date_pdf_received;
  const trrReceived = row.date_trr_received;
  const trrRequired = Boolean(row.trr_required);
  const allFilesReceived = !!(pdfReceived && (!trrRequired || trrReceived));

  return (
    <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-slate-50/80 border-b border-slate-200">
            <Label>GP ID</Label>
            <Label>PDF received</Label>
            <Label>TRR received</Label>
            <Label>All files</Label>
            <Label>TRR required</Label>
            <Label>First production</Label>
            <Label>Deadline</Label>
            <Label>Initial notification</Label>
            <Label>Chaser 1</Label>
            <Label>Chaser 2</Label>
            <Label>Escalation</Label>
            <Label>Comment</Label>
          </tr>
        </thead>
        <tbody>
          <tr>
            <Cell>
              <TextCell value={row.r1_gp_id_fonds} />
            </Cell>
            <Cell>
              <DateCell
                value={pdfReceived}
                fileName={row.pdf_file_name as string | null}
                uploadId={row.pdf_upload_id as number | null}
              />
            </Cell>
            <Cell>
              <DateCell
                value={trrReceived}
                fileName={row.trr_file_name as string | null}
                uploadId={row.trr_upload_id as number | null}
              />
            </Cell>
            <Cell>
              {allFilesReceived ? (
                <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-xs font-medium text-emerald-700 ring-1 ring-emerald-200">
                  ✓ Yes
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700 ring-1 ring-amber-200">
                  Pending
                </span>
              )}
            </Cell>
            <Cell>
              <BoolCell value={row.trr_required} />
            </Cell>
            <Cell>
              <BoolCell value={row.first_production} />
            </Cell>
            <Cell>
              {row.deadline_nb_days != null ? (
                <span className="text-sm text-slate-800">
                  {String(row.deadline_nb_days)}d
                </span>
              ) : (
                <span className="text-xs text-slate-300">—</span>
              )}
            </Cell>
            <Cell>
              <DateCell value={row.date_initial_notification} />
            </Cell>
            <Cell>
              <DateCell value={row.date_chaser_1} />
            </Cell>
            <Cell>
              <DateCell value={row.date_chaser_2} />
            </Cell>
            <Cell>
              <DateCell value={row.date_escalation} />
            </Cell>
            <Cell>
              <PortfolioCommentCell
                fundId={fundId}
                fundName={String(row.fund_name ?? "")}
                quarter={quarter}
                lastCommentMessage={row.last_comment_message as string | null}
                lastCommentUserName={
                  row.last_comment_user_name as string | null
                }
                lastCommentUserEmail={
                  row.last_comment_user_email as string | null
                }
                lastCommentAt={row.last_comment_at as string | null}
              />
            </Cell>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
